import { academicService } from "./academicService";
import { studentService, instructorService, subjectService } from "./entityService";
import { moderatorService } from "./moderatorService";
import { roleService } from "./roleService";
import { notificationService } from "./notificationService";
import { adminJoinRequestsService } from "./adminJoinRequestsService";
import { adminStudentCoursesService } from "./adminStudentCoursesService";
import { adminInstructorCoursesService } from "./adminInstructorCoursesService";
import type { AdminInstructorCoursePayload } from "./adminInstructorCoursesService";
import { adminInstructorFinancialService } from "./adminInstructorFinancialService";
import { adminWalletService } from "./adminWalletService";
import type { AdminWalletAdjustPayload } from "./adminWalletService";
import { adminStudentDevicesService } from "./adminStudentDevicesService";

export const adminService = {
  // Students
  async getStudents(params?: Record<string, unknown>) {
    return studentService.list(params);
  },

  async getStudent(id: string | number) {
    return studentService.show(id);
  },

  async createStudent(data: Record<string, unknown>) {
    return studentService.create(data);
  },

  async updateStudent(id: string | number, data: Record<string, unknown>) {
    return studentService.update(id, data);
  },

  async deleteStudent(id: string | number) {
    return studentService.delete(id);
  },

  async blockStudent(id: string | number) {
    return studentService.block(id);
  },

  async unblockStudent(id: string | number) {
    return studentService.unblock(id);
  },

  async getStudentCourses(id: string | number) {
    return adminStudentCoursesService.list(id);
  },

  async getStudentCourseOptions(id: string | number) {
    return adminStudentCoursesService.options(id);
  },

  async attachStudentCourse(id: string | number, courseId: number, data: Record<string, unknown>) {
    return adminStudentCoursesService.attach(id, courseId, data);
  },

  async updateStudentCourse(id: string | number, courseId: number, data: Record<string, unknown>) {
    return adminStudentCoursesService.update(id, courseId, data);
  },

  async removeStudentCourse(id: string | number, courseId: number) {
    return adminStudentCoursesService.remove(id, courseId);
  },

  async getInstructorCourses(id: string | number) {
    return adminInstructorCoursesService.list(id);
  },

  async getInstructorCourseDetails(id: string | number, courseId: number) {
    return adminInstructorCoursesService.details(id, courseId);
  },

  async updateInstructorCourse(id: string | number, courseId: number, data: AdminInstructorCoursePayload) {
    return adminInstructorCoursesService.update(id, courseId, data);
  },

  async removeInstructorCourse(id: string | number, courseId: number) {
    return adminInstructorCoursesService.remove(id, courseId);
  },

  async getInstructorFinancialOverview(id: string | number) {
    return adminInstructorFinancialService.overview(id);
  },

  async getInstructorFinancialTransactions(id: string | number, params?: { page?: number; limit?: number; status?: string }) {
    return adminInstructorFinancialService.transactions(id, params);
  },

  async getInstructorPayouts(id: string | number, params?: { page?: number; limit?: number }) {
    return adminInstructorFinancialService.payouts(id, params);
  },

  async createInstructorPayout(id: string | number, payload: Parameters<typeof adminInstructorFinancialService.createPayout>[1]) {
    return adminInstructorFinancialService.createPayout(id, payload);
  },

  async getWalletSummary() {
    return adminWalletService.summary();
  },

  async getWallets(params?: Parameters<typeof adminWalletService.list>[0]) {
    return adminWalletService.list(params);
  },

  async getStudentWallet(id: string | number) {
    return adminWalletService.overview(id);
  },

  async getStudentWalletLedger(id: string | number, params?: { page?: number; limit?: number; type?: string }) {
    return adminWalletService.ledger(id, params);
  },

  async adjustStudentWallet(id: string | number, payload: AdminWalletAdjustPayload) {
    return adminWalletService.adjust(id, payload);
  },

  async getStudentAppleTopups(id: string | number, params?: { page?: number; limit?: number }) {
    return adminWalletService.studentAppleTopups(id, params);
  },

  async getAppleWalletTopups(params?: Parameters<typeof adminWalletService.appleTopups>[0]) {
    return adminWalletService.appleTopups(params);
  },

  async getWalletTopupRequests(params?: Parameters<typeof adminWalletService.topupRequests>[0]) {
    return adminWalletService.topupRequests(params);
  },

  async approveWalletTopupRequest(
    id: string | number,
    payload?: Parameters<typeof adminWalletService.approveTopupRequest>[1]
  ) {
    return adminWalletService.approveTopupRequest(id, payload);
  },

  async rejectWalletTopupRequest(
    id: string | number,
    payload?: Parameters<typeof adminWalletService.rejectTopupRequest>[1]
  ) {
    return adminWalletService.rejectTopupRequest(id, payload);
  },

  async getStudentDevices(id: string | number) {
    return adminStudentDevicesService.devices(id);
  },

  async removeStudentDevice(id: string | number, deviceRowId: string | number) {
    return adminStudentDevicesService.removeDevice(id, deviceRowId);
  },

  async getStudentLoginLogs(
    id: string | number,
    params?: { page?: number; limit?: number; status?: string }
  ) {
    return adminStudentDevicesService.loginLogs(id, params);
  },

  // Instructors
  async getInstructors(params?: Record<string, unknown>) {
    return instructorService.list(params);
  },

  async getInstructor(id: string | number) {
    return instructorService.show(id);
  },

  async createInstructor(data: Record<string, unknown>) {
    return instructorService.create(data);
  },

  async updateInstructor(id: string | number, data: Record<string, unknown>) {
    return instructorService.update(id, data);
  },

  async deleteInstructor(id: string | number) {
    return instructorService.delete(id);
  },

  async blockInstructor(id: string | number) {
    return instructorService.block(id);
  },

  async unblockInstructor(id: string | number) {
    return instructorService.unblock(id);
  },

  // Subjects
  async getSubjects(params?: Record<string, unknown>) {
    return subjectService.list(params);
  },

  async getSubject(id: string | number) {
    return subjectService.show(id);
  },

  async createSubject(data: Record<string, unknown>) {
    return subjectService.create(data);
  },

  async updateSubject(id: string | number, data: Record<string, unknown>) {
    return subjectService.update(id, data);
  },

  async deleteSubject(id: string | number) {
    return subjectService.delete(id);
  },

  // Roles & Permissions
  async getRoles() {
    return roleService.listRoles();
  },

  async getRole(id: string | number) {
    return roleService.showRole(id);
  },

  async createRole(data: Record<string, unknown>) {
    return roleService.createRole(data);
  },

  async updateRole(id: string | number, data: Record<string, unknown>) {
    return roleService.updateRole(id, data);
  },

  async deleteRole(id: string | number) {
    return roleService.deleteRole(id);
  },

  async getPermissions() {
    return roleService.listPermissions();
  },

  // Notifications
  async getNotifications(params?: Record<string, unknown>) {
    return notificationService.list(params);
  },

  async createNotification(data: Record<string, unknown>) {
    return notificationService.create(data as Parameters<typeof notificationService.create>[0]);
  },

  async deleteNotification(id: string | number) {
    return notificationService.delete(id);
  },

  // Course join requests (admin review)
  async getJoinRequests(params?: Parameters<typeof adminJoinRequestsService.list>[0]) {
    return adminJoinRequestsService.list(params);
  },

  async approveJoinRequest(id: number, data: Parameters<typeof adminJoinRequestsService.approve>[1]) {
    return adminJoinRequestsService.approve(id, data);
  },

  async rejectJoinRequest(id: number, reason?: string) {
    return adminJoinRequestsService.reject(id, reason);
  },

  // Moderators
  async getModerators(params?: Record<string, unknown>) {
    return moderatorService.list(params);
  },

  async getModerator(id: string | number) {
    return moderatorService.show(id);
  },

  async createModerator(data: Record<string, unknown>) {
    return moderatorService.create(data);
  },

  async updateModerator(id: string | number, data: Record<string, unknown>) {
    return moderatorService.update(id, data);
  },

  async deleteModerator(id: string | number) {
    return moderatorService.delete(id);
  },

  // Academic
  getGrades: academicService.listGrades,
};
